(ns web3.components.persisted
  (:require [web3.components.text :as text]
            [web3.components.search :as search]
            [web3.components.autocomplete :as autocomplete]
            [web3.components.select :as select]
            [web3.components.upload :as upload]
            [web3.view :as v]
            [reagent.core :as r]
            [web3.utils :as utils :refer [save-event?]]
            [web3.persistence.graphql :as g]
            [web3.forms.validation :as validation :refer [validate-field]]
            [web3.components.parent :as parent]
            [web3.i18n :as i]
            [web3.modals.forms :as forms]
            [web3.view.hiccup :as hiccup]
            [web3.icons.shapes :as shapes]
            [web3.icons.actions :as actions]))

(defn undo-event? [^js e]
  (= 27 (.-which e)))

(defn mark-dirty
  "Individual fields should be marked 'dirty' when the user
   has entered and then left the field, or attempted to persist
   the field."
  [state]
  (when-not (:dirty? @state)
    (swap! state assoc :dirty? true)))

(defn input-change [{:keys [state]} ^react/SyntheticEvent e]
  (swap! state assoc :local-value (.. e -target -value)))

(defn get-undo-value
  "Get the current `undo` value for the field.

   For a self-contained field, this is the persisted value.
   For a controlled component, this is the last-focused-value."
  [{:keys [state props] :as this}]
  (if (:persist-value! props)
    (:persisted-value props)
    (let [{:keys [value default-value change-value!]} props
          {:keys [last-focused-value]} @state]
      (or last-focused-value
          value
          default-value))))

(defn undo!
  "Set the field's value to the undo value."
  [{{:keys [persist-value! change-value!]} :props
    state :state
    :as this}]
  (let [value (get-undo-value this)]
    (cond persist-value! (swap! state assoc :local-value value)
          change-value! (change-value! value))))

(defn persist-local-state!
  "Attempt to persist the current local value for a field."
  [{:keys [state]
    {:keys [persist-value!]} :props
    :as ^js this}]
  (when-not (v/mounted? this) (println :WARN "persist-local-state! on unmounted component" this))
  (when (and (v/mounted? this)
             (contains? @state :local-value))
    (persist-value! this (:local-value @state)
                    {:on-complete (fn []
                                    (swap! state assoc :ephemeral-helptext (i/get :i/saved))
                                    (js/setTimeout #(when (v/mounted? this)
                                                      (swap! state dissoc :ephemeral-helptext))
                                                   1500))})))

(defn get-current-value
  "The 'current' local value for a field.

  Individually-persisted fields are detected by the presence of the `:persist-value!` key,
  and keep their own local state."
  [{:keys [state]
    {:keys [persist-value! persisted-value value default-value]} :props}]
  (if persist-value!
    (or (:local-value @state)
        persisted-value
        default-value)
    (or value
        default-value)))

(defn wrap-error [message]
  [:span.text-red {:key message} message])

(defn format-messages
  "Formats messages returned by validation functions.
   Expects the presence of :error, :invalid, :warn, and/or :info keys."
  [{:keys [error invalid warn info]}]
  (let [error-messages (some->> (concat error invalid warn)
                                (keep (comp utils/some-str :message))
                                (seq)
                                (map wrap-error))
        other-messages (seq (for [{:keys [message]} info
                                  :when message]
                              [:span.dark-gray {:key message} message]))]
    (seq (concat error-messages other-messages))))

(defn field-validations
  "Returns validations for field.

   Expects :validators prop.
   Passes :label as extra info to validator, if present."
  [{{:keys [validators] :as props} :props :as this}]
  (validation/validate-field
   validators
   (get-current-value this)
   (select-keys props [:label])))

(defn pass-props [props]
  (-> props
      (dissoc :persisted-value
              :persist-value!
              :change-value!
              :on-save
              :default-value)))

(defn diff? [{{:keys [value
                      persisted-value
                      persist-value!]} :props
              state :state}]
  (let [persisted-value (or persisted-value value)
        {:keys [local-value]} @state]
    (if persist-value!
      (and (some? local-value) (not= persisted-value local-value))
      true)))

(v/defview text-input
  "Persisted text input.

  Validates input and formats relevant validation messages.

  The text input will be persisted on blur, or when the user
  presses 'enter'. A loading indicator will be displayed, and
  persistence errors will be displayed with the same format as
  validation messages.

  ---- General 'Persisted Field' notes ----

  A persisted field supports input validation, tracks its own 'dirty'
  state, and knows how to display validation messages and mutation state.

  Two modes of usage:

  1. Self-Contained (no external state except a value-from-the-db)
  2. Controlled (state is stored 'above' the component and passed down)

  A self-contained field requires the following props:

  - `:persist-value!`, a function of [<component>, <value>]
     which should handle the mutation and update the component's
     mutation state.

  - `:persisted-value`, the field's value as received from the db.
    Persisted components are _not_ 'controlled components'; they
    maintain local state and only persist to the db when the user
    saves the field.

  A controlled field accepts the following props:

  - `:change-value!`, a function which should accept a value and
    sets it into app state. This is similar to `:on-change`, but
    is called with the latest _value_ instead of the event. (This
    allows components to be implemented more flexibly.)

  - `:value`, the current value for the form from app state.

  - `:on-save`, a function which will be called when the user
    initiates a 'save' action by pressing 'Enter' or clicking
    the checkmark icon.

  Additionally, all persisted fields accept the following props:

  - `:validators`, a list of validators, eg. [[:min-length 2]]
    (see web3.forms.validation)
  - `:loading?`
  - `:dirty?`

  Other props are passed down to the underlying text component."
  ;; TODO
  ;; - X and ✓ for cancel/save
  [{:keys [persist-value!
           change-value!
           on-save
           read-only?
           helptext

           on-blur
           on-focus
           content/inline-right

           on-change
           validators

           kind
           depends-on]
    :as props}]
  (let [{:keys [state] :as this} (r/current-component)
        {:keys [local-value
                focused?
                ephemeral-helptext]} @state
        {:keys [graphql/loading? graphql/error]} (g/mutation-state)
        form-data (forms/form-data)
        dependent-form-data (get form-data depends-on)

        saveable? (or on-save persist-value!)
        current-diff? (diff? this)
        dirty? (if (some? (:dirty? props))
                 (:dirty? props)
                 (:dirty? @state))
        current-value (get-current-value this)
        undo-value (get-undo-value this)
        validations (field-validations this)
        invalid? (seq (:invalid validations))

        save! (fn [e]
                (when (and saveable? (diff? this))
                  (when (.-preventDefault e)
                    (.preventDefault e))
                  (mark-dirty state)
                  (when (not invalid?)
                    (if on-save
                      (on-save (get-current-value this))
                      (persist-local-state! this)))))
        text-element (case kind :autocomplete autocomplete/autocomplete
                                :search search/search-entities
                                :model-search search/search-models
                                :parent-selector parent/parent-selector
                                text/input)]
    [text-element
     (-> (pass-props (dissoc props :kind))
         (cond-> (not (contains? (set validators)
                                 :required))
                 (assoc :placeholder-right [:div.text-dark-gray (i/get :i/optional)]))
         (cond-> (some? dependent-form-data) (assoc :depends-on dependent-form-data))
         (assoc :value current-value
                :on-blur #(do (swap! state assoc :focused? false :dirty? true)
                              (when (and persist-value! current-diff? (not invalid?))
                                (persist-local-state! this))
                              (when on-blur (on-blur %)))
                :on-focus #(do (swap! state assoc :focused? true :last-focused-value current-value)
                               (when on-focus (on-focus %)))
                :on-change #()
                :ephemeral-helptext ephemeral-helptext
                :invalid? (and dirty? invalid?)
                :helptext (or (some->> error :message (wrap-error))
                              ephemeral-helptext
                              (when dirty? (format-messages (merge validations
                                                                   @state)))
                              helptext)
                :helptext-pad? true

                :content/inline-right (or inline-right
                                          [:div.flex.items-center
                                           (when (and focused?
                                                      (not= current-value undo-value)
                                                      (not loading?))
                                             [:div.margin-left-1.text-dark-gray.cursor-pointer.hover-text-black-hover.transition-all
                                              {:on-mouse-down #(do
                                                                 (.preventDefault %)
                                                                 (undo! this))}
                                              (-> actions/undo
                                                  (hiccup/size 20))])
                                           (when (and (or on-save persist-value!)
                                                      current-diff?
                                                      focused?
                                                      (not loading?)
                                                      (not invalid?))
                                             [:div.margin-left-1.text-dark-gray.hover-text-black-hover.cursor-pointer.active-text-black-hover
                                              {:on-mouse-down save!}
                                              (-> shapes/checkmark-thick
                                                  (hiccup/size 18))])]))
         (merge (cond read-only? {:on-change #()}           ;; sets component as 'controlled'
                      (or on-change
                          change-value!) {:on-change (fn [e]
                                                       (when on-change (on-change e))
                                                       (change-value! (.. e -target -value)))
                                          :on-key-down (fn [e]
                                                         (when (undo-event? e)
                                                           (undo! this))
                                                         (when (and on-save (save-event? this e))
                                                           (on-save current-value)))}
                      persist-value! {:on-key-down (fn [^js e]
                                                     (cond (undo-event? e)
                                                           (undo! this)
                                                           (and current-diff?
                                                                (save-event? this e))
                                                           (save! e)))
                                      :on-save save!
                                      :loading? loading?
                                      :on-change #(input-change this %)})))]))

(v/defview select
  "Persisted select input.

  Usage is similar to the persisted text input.

  When in self-contained mode (with a `persist-value!` prop),
  the component will persist immediately when the user selects
  a new value."
  [{:keys [on-change
           persist-value!
           change-value!

           read-only?] :as props} & options]
  (let [{:keys [state] :as this} (r/current-component)
        {:keys [focused?]} @state
        {:keys [graphql/loading? graphql/error]} (g/mutation-state)
        dirty? (or (:dirty? props) (:dirty? @state))
        current-value (get-current-value this)
        validations (field-validations this)
        invalid? (seq (:invalid validations))]
    [select/select (-> (pass-props props)
                       (assoc :value current-value
                              :loading? loading?
                              :invalid? (and dirty? invalid?)
                              :focused? focused?)
                       (cond-> (not read-only?)
                               (merge {:on-change (fn [e]
                                                    (let [next-value (.. e -target -value)]
                                                      (mark-dirty state)
                                                      (cond on-change (on-change e)
                                                            change-value! (change-value! next-value)
                                                            persist-value! (persist-value! this next-value))))
                                       :on-blur #(mark-dirty state)
                                       :loading? loading?
                                       :helptext (when dirty? (format-messages (merge validations
                                                                                      {:error error})))
                                       :helptext-persistent? dirty?})))
     options]))

(v/defview file-upload
  [{:keys [on-change
           persist-value!
           change-value!] :as props}]
  (let [{:keys [state] :as this} (r/current-component)
        {:keys [graphql/loading? graphql/error]} (g/mutation-state)
        dirty? (or (:dirty? props) (:dirty? @state))
        current-value (get-current-value this)
        validations (field-validations this)
        invalid? (seq (:invalid validations))]
    [upload/file (-> (pass-props props)
                     (assoc :default-value current-value
                            :loading? loading?
                            :invalid? (and dirty? invalid?)
                            :on-clear (fn []
                                        (when-let [change (or on-change change-value!)]
                                          (change nil)))
                            :on-upload (fn [{:keys [location]}]
                                         (mark-dirty state)
                                         (cond on-change (on-change location)
                                               change-value! (change-value! location)
                                               persist-value! (persist-value! this location)))
                            :helptext (when dirty? (format-messages (merge validations
                                                                           {:error error})))
                            :helptext-persistent? true))]))
